/**
 * Components for the experience strategy
 */

import React from 'react';
import { Box, Typography, Slider, Button, Chip, Badge } from '@mui/material';
import { COLORS } from '../../../config/colors';
import { EXPERIENCE_LEVELS } from './constants';
import { getNextStrategy } from './strategy';
import { StrategyPhase } from '../baseStrategy';
import { runAIMatches, runMultiMatches } from '../../api';

/**
 * Read-only version of the experience histogram for chat history
 * @param {Array} selectedRange - The selected experience range [min, max]
 * @param {number} candidateCount - The number of candidates in the selected range
 * @returns {JSX.Element} The read-only histogram visualization component
 */
export const ExperienceHistogramReadOnly = ({
  selectedRange = [2, 17],
  candidateCount = 12000
}) => {
  // Format candidate count for display
  const formatCandidateCount = (count) => {
    if (count < 1000) {
      return count.toString();
    } else if (count < 10000) {
      return Math.round(count / 1000) + 'K';
    } else if (count < 100000) {
      return Math.round(count / 1000) + 'K';
    } else if (count < 1000000) {
      return Math.round(count / 1000) + 'K';
    } else {
      return Math.round(count / 1000000) + 'M';
    }
  };

  // Generate sparkline data points for the chart
  const generateSparklineData = () => {
    const points = [];
    const numPoints = 50;
    
    for (let i = 0; i <= numPoints; i++) {
      const x = (i / numPoints) * 100; // 0 to 100%
      const yearValue = (i / numPoints) * 30; // 0 to 30 years
      
      // Create a realistic distribution curve with peaks around 5-15 years
      const center1 = 8;
      const center2 = 15;
      const spread = 4;
      
      const height1 = Math.exp(-Math.pow(yearValue - center1, 2) / (2 * Math.pow(spread, 2)));
      const height2 = Math.exp(-Math.pow(yearValue - center2, 2) / (2 * Math.pow(spread * 1.5, 2))) * 0.6;
      
      const combinedHeight = Math.max(0.1, (height1 + height2) * 60); // Scale to 0-60%
      
      points.push({ x, y: 100 - combinedHeight, yearValue }); // Invert Y for SVG coordinates
    }
    
    return points;
  };

  const sparklineData = generateSparklineData();

  // Create SVG path for the sparkline
  const createSparklinePath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    // Create smooth curve using quadratic bezier curves
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2;
      path += ` Q ${cpx} ${cpy} ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  // Create filled area path
  const createFilledPath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} 100`; // Start from bottom
    
    // Go to first point
    path += ` L ${points[0].x} ${points[0].y}`;
    
    // Create smooth curve
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2;
      path += ` Q ${cpx} ${cpy} ${curr.x} ${curr.y}`;
    }
    
    // Close the path to create filled area
    path += ` L ${points[points.length - 1].x} 100 Z`;
    
    return path;
  };

  // Filter points for selected range
  const selectedPoints = sparklineData.filter(point => 
    point.yearValue >= selectedRange[0] && point.yearValue <= selectedRange[1]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '600px' }}>
      {/* Main card container matching Figma design */}
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px solid rgba(192, 197, 205, 0.5)',
          bgcolor: '#FFFFFF',
          overflow: 'visible', // Changed to visible to prevent clipping
          minHeight: '340px', // Increased height to accommodate slider
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            borderBottom: '1px solid #EAECF0',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Typography 
            sx={{ 
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '1.375em',
              color: '#101828'
            }}
          >
            Work Experience (Selected)
          </Typography>
        </Box>

        {/* Chart Content */}
        <Box
          sx={{
            padding: '20px 20px 32px', // Increased bottom padding
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1
          }}
        >
          {/* Stats */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              padding: '12px',
              marginBottom: '20px'
            }}
          >
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '18px',
                lineHeight: '1.333em',
                color: '#101828'
              }}
            >
              {selectedRange[0]} - {selectedRange[1]} years
            </Typography>
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '1.385em',
                color: '#667085'
              }}
            >
              {formatCandidateCount(candidateCount)} candidates
            </Typography>
          </Box>

          {/* Sparkline Chart */}
          <Box
            sx={{
              width: '100%',
              height: '120px',
              position: 'relative',
              marginBottom: '20px'
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ position: 'absolute', top: 0, left: 0 }}
            >
              {/* Background (disabled) area */}
              <path
                d={createFilledPath(sparklineData)}
                fill="url(#backgroundGradientReadOnly)"
                opacity="0.2"
              />
              <path
                d={createSparklinePath(sparklineData)}
                stroke="#167FF9"
                strokeWidth="2.5"
                fill="none"
                opacity="0.2"
              />
              
              {/* Active (selected) area */}
              {selectedPoints.length > 1 && (
                <>
                  <path
                    d={createFilledPath(selectedPoints)}
                    fill="url(#activeGradientReadOnly)"
                  />
                  <path
                    d={createSparklinePath(selectedPoints)}
                    stroke="#167FF9"
                    strokeWidth="2.5"
                    fill="none"
                  />
                </>
              )}
              
              {/* Gradient definitions */}
              <defs>
                <linearGradient id="backgroundGradientReadOnly" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#167FF9" stopOpacity="1" />
                  <stop offset="81.25%" stopColor="#167FF9" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="activeGradientReadOnly" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#167FF9" stopOpacity="1" />
                  <stop offset="81.25%" stopColor="#167FF9" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </Box>

          {/* Range Slider */}
          <Box sx={{ width: '100%', position: 'relative', paddingBottom: '10px' }}>
            <Box
              sx={{
                height: '4px',
                bgcolor: '#EAECF0',
                borderRadius: '20px',
                position: 'relative'
              }}
            >
              {/* Selected range track */}
              <Box
                sx={{
                  position: 'absolute',
                  left: `${(selectedRange[0] / 30) * 100}%`,
                  width: `${((selectedRange[1] - selectedRange[0]) / 30) * 100}%`,
                  height: '4px',
                  bgcolor: '#167FF9',
                  borderRadius: '20px'
                }}
              />
              {/* Start thumb */}
              <Box
                sx={{
                  position: 'absolute',
                  left: `${(selectedRange[0] / 30) * 100}%`,
                  top: '-8px',
                  width: '20px',
                  height: '20px',
                  bgcolor: '#167FF9',
                  border: '2px solid #FFFFFF',
                  borderRadius: '50%',
                  transform: 'translateX(-50%)'
                }}
              />
              {/* End thumb */}
              <Box
                sx={{
                  position: 'absolute',
                  left: `${(selectedRange[1] / 30) * 100}%`,
                  top: '-8px',
                  width: '20px',
                  height: '20px',
                  bgcolor: '#167FF9',
                  border: '2px solid #FFFFFF',
                  borderRadius: '50%',
                  transform: 'translateX(-50%)'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Interactive experience histogram component matching Figma design
 */
export const ExperienceHistogram = ({
  histogramData,
  onRangeSelected,
  advanceToNextTopic,
  setMessages,
  handleRefreshResults,
  onSendText,
  messages = [],
  defaultExperienceRange = [2, 17],
  levelCounts = {}
}) => {
  const [value, setValue] = React.useState(defaultExperienceRange);
  const [isConfirmed, setIsConfirmed] = React.useState(false);
  const [candidatesInRange, setCandidatesInRange] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  // Format candidate count for display
  const formatCandidateCount = (count) => {
    if (count < 1000) {
      return count.toString();
    } else if (count < 10000) {
      return Math.round(count / 1000) + 'K';
    } else if (count < 100000) {
      return Math.round(count / 1000) + 'K';
    } else if (count < 1000000) {
      return Math.round(count / 1000) + 'K';
    } else {
      return Math.round(count / 1000000) + 'M';
    }
  };

  // Function to fetch candidate count for a specific experience range
  const fetchCandidateCount = React.useCallback(async (experienceRange) => {
    try {
      setIsLoading(true);
      
      // Get the base requirements from the current AI matches
      const aiMatches = await runAIMatches(messages);
      if (!aiMatches || !aiMatches.search_gen_response || !aiMatches.search_gen_response.icp_requirements) {
        console.warn('No base requirements found for experience count');
        return 0;
      }
      
      const baseRequirements = aiMatches.search_gen_response.icp_requirements;
      
      // Create experience requirement for the selected range
      const [startYear, endYear] = experienceRange;
      const startMonth = startYear * 12;
      const endMonth = endYear * 12;
      
      const experienceRequirement = {
        bool_str: `(workexp.overall_experience:${startMonth}-${endMonth})`,
        section_id: 'experience_range_count',
        section_name: 'Overall Years of Experience',
        section_type: 'Must Have'
        // Note: macro_id is NOT set here - it will be assigned by the API
      };
      
      // Run multiMatches with the experience requirement in the third parameter (to_match_requirements)
      const multiMatchesResult = await runMultiMatches(
        baseRequirements,
        [],
        [experienceRequirement]
      );
      
      // The API assigns macro_id and returns it in target_requirements
      if (multiMatchesResult && multiMatchesResult.target_requirements && multiMatchesResult.count_by_queries) {
        // Find the requirement that matches our section_id
        const matchingReq = multiMatchesResult.target_requirements.find(req => 
          req.section_id === 'experience_range_count'
        );
        
        if (matchingReq && matchingReq.macro_id) {
          const count = multiMatchesResult.count_by_queries[matchingReq.macro_id] || 0;
          console.log(`Experience range ${startYear}-${endYear} years: ${count} candidates (macro_id: ${matchingReq.macro_id})`);
          return count;
        }
      }
      
      return 0;
    } catch (error) {
      console.error('Error fetching candidate count:', error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [messages]);

  // Debounced function to update candidate count when slider changes
  const debouncedFetchCount = React.useCallback(
    React.useMemo(() => {
      let timeoutId;
      return (experienceRange) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchCandidateCount(experienceRange).then(count => {
            setCandidatesInRange(count);
          });
        }, 500); // 500ms debounce
      };
    }, [fetchCandidateCount]),
    [fetchCandidateCount]
  );

  // Effect to fetch initial count and when value changes
  React.useEffect(() => {
    debouncedFetchCount(value);
  }, [value, debouncedFetchCount]);

  // Generate sparkline data points for the chart
  const generateSparklineData = () => {
    const points = [];
    const numPoints = 50;
    
    for (let i = 0; i <= numPoints; i++) {
      const x = (i / numPoints) * 100; // 0 to 100%
      const yearValue = (i / numPoints) * 30; // 0 to 30 years
      
      // Create a realistic distribution curve with peaks around 5-15 years
      const center1 = 8;
      const center2 = 15;
      const spread = 4;
      
      const height1 = Math.exp(-Math.pow(yearValue - center1, 2) / (2 * Math.pow(spread, 2)));
      const height2 = Math.exp(-Math.pow(yearValue - center2, 2) / (2 * Math.pow(spread * 1.5, 2))) * 0.6;
      
      const combinedHeight = Math.max(0.1, (height1 + height2) * 60); // Scale to 0-60%
      
      points.push({ x, y: 100 - combinedHeight, yearValue }); // Invert Y for SVG coordinates
    }
    
    return points;
  };

  const sparklineData = generateSparklineData();

  // Create SVG path for the sparkline
  const createSparklinePath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    // Create smooth curve using quadratic bezier curves
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2;
      path += ` Q ${cpx} ${cpy} ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  // Create filled area path
  const createFilledPath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} 100`; // Start from bottom
    
    // Go to first point
    path += ` L ${points[0].x} ${points[0].y}`;
    
    // Create smooth curve
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2;
      path += ` Q ${cpx} ${cpy} ${curr.x} ${curr.y}`;
    }
    
    // Close the path to create filled area
    path += ` L ${points[points.length - 1].x} 100 Z`;
    
    return path;
  };

  // Filter points for selected range
  const selectedPoints = sparklineData.filter(point => 
    point.yearValue >= value[0] && point.yearValue <= value[1]
  );

  // Handle confirm button click
  const handleConfirm = () => {
    setIsConfirmed(true);
    
    const displayText = `${value[0]}-${value[1]} years of experience`;
    const rangeValues = { startYear: value[0], endYear: value[1], displayText };

    if (setMessages) {
      // Add the read-only histogram component to chat interface
      const userMessage = {
        role: 'User',
        content: `I've selected candidates with ${displayText}.`,
        component: ExperienceHistogramReadOnly,
        componentProps: {
          selectedRange: [value[0], value[1]],
          candidateCount: candidatesInRange
        }
      };

      // Add AI summary message
      const aiSummaryMessage = {
        role: 'AI',
        content: `Perfect! I've noted that you're looking for candidates with ${displayText}. This gives us ${formatCandidateCount(candidatesInRange)} potential candidates to work with. Let's move on to the next topic to further refine your search criteria.`
      };

      // Update messages with both the user selection and AI summary
      setMessages(prev => [...prev, userMessage, aiSummaryMessage]);
      
      // Refresh results with the new messages
      if (handleRefreshResults) {
        const allMessages = [...messages, userMessage, aiSummaryMessage];
        handleRefreshResults(allMessages);
      }
      
      // Advance to next topic
      if (advanceToNextTopic) {
        setTimeout(() => {
          const updatedDiscoveryState = advanceToNextTopic();
          if (updatedDiscoveryState && updatedDiscoveryState.currentDiscoTopic && onSendText) {
            const moveOnMessage = `I am done with my selections for Experience and am ready to move on to the next topic.`;
            setTimeout(() => {
              onSendText(moveOnMessage);
            }, 200);
          }
        }, 100);
      }
      
      // Call the callback if provided
      if (onRangeSelected) {
        onRangeSelected(rangeValues);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '600px' }}>
      {/* Main card container matching Figma design */}
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px solid rgba(192, 197, 205, 0.5)',
          bgcolor: '#FFFFFF',
          overflow: 'visible', // Changed to visible to prevent clipping
          minHeight: '340px', // Increased height to accommodate slider
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '12px'
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            borderBottom: '1px solid #EAECF0',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Typography 
            sx={{ 
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '1.375em',
              color: '#101828'
            }}
          >
            Work Experience
          </Typography>
        </Box>

        {/* Chart Content */}
        <Box
          sx={{
            padding: '20px 20px 32px', // Increased bottom padding
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1
          }}
        >
          {/* Stats */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              padding: '12px',
              marginBottom: '20px'
            }}
          >
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '18px',
                lineHeight: '1.333em',
                color: '#101828'
              }}
            >
              {value[0]} - {value[1]} years
            </Typography>
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '1.385em',
                color: isLoading ? '#999' : '#667085'
              }}
            >
              {isLoading ? 'Loading...' : `${formatCandidateCount(candidatesInRange)} candidates`}
            </Typography>
          </Box>

          {/* Sparkline Chart */}
          <Box
            sx={{
              width: '100%',
              height: '100px',
              position: 'relative',
              marginBottom: '20px'
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ position: 'absolute', top: 0, left: 0 }}
            >
              {/* Background (disabled) area */}
              <path
                d={createFilledPath(sparklineData)}
                fill="url(#backgroundGradient)"
                opacity="0.2"
              />
              <path
                d={createSparklinePath(sparklineData)}
                stroke="#167FF9"
                strokeWidth="2.5"
                fill="none"
                opacity="0.2"
              />
              
              {/* Active (selected) area */}
              {selectedPoints.length > 1 && (
                <>
                  <path
                    d={createFilledPath(selectedPoints)}
                    fill="url(#activeGradient)"
                  />
                  <path
                    d={createSparklinePath(selectedPoints)}
                    stroke="#167FF9"
                    strokeWidth="2.5"
                    fill="none"
                  />
                </>
              )}
              
              {/* Gradient definitions */}
              <defs>
                <linearGradient id="backgroundGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#167FF9" stopOpacity="1" />
                  <stop offset="81.25%" stopColor="#167FF9" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="activeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#167FF9" stopOpacity="1" />
                  <stop offset="81.25%" stopColor="#167FF9" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </Box>

          {/* Range Slider - Fixed positioning to prevent cropping */}
          <Box sx={{ width: '100%', position: 'relative', paddingBottom: '20px' }}>
            <Slider
              value={value}
              onChange={(event, newValue) => setValue(newValue)}
              min={0}
              max={30}
              sx={{
                color: '#167FF9',
                height: '4px',
                padding: '15px 0',
                '& .MuiSlider-track': { 
                  bgcolor: '#167FF9', 
                  height: '4px',
                  border: 'none',
                  borderRadius: '20px'
                },
                '& .MuiSlider-thumb': {
                  bgcolor: '#167FF9',
                  border: '2px solid #FFFFFF',
                  width: '20px',
                  height: '20px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 0 0 8px rgba(22, 127, 249, 0.16)'
                  },
                  '&:focus, &:hover, &.Mui-active': {
                    boxShadow: '0 0 0 8px rgba(22, 127, 249, 0.16)'
                  },
                  '&:before': {
                    display: 'none'
                  }
                },
                '& .MuiSlider-rail': { 
                  bgcolor: '#EAECF0', 
                  height: '4px',
                  borderRadius: '20px'
                },
                '& .MuiSlider-valueLabel': {
                  display: 'none'
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Confirm Button */}
      <Button
        variant='contained'
        onClick={handleConfirm}
        disabled={isConfirmed}
        sx={{
          bgcolor: '#4599FA',
          color: '#FFFFFF',
          fontFamily: 'Roboto',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '1.4285714285714286em',
          textTransform: 'none',
          borderRadius: '8px',
          padding: '8px 12px',
          height: '36px',
          gap: '6px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '&:hover': { 
            bgcolor: '#3A7BD5' 
          },
          '&.Mui-disabled': {
            bgcolor: 'rgba(69, 153, 250, 0.3)',
            color: '#FFFFFF'
          }
        }}
      >
        {isConfirmed ? 'Work Experience Confirmed' : 'Confirm Work Experience'}
      </Button>
    </Box>
  );
};

// Export ExperienceOptions as an alias of ExperienceHistogram for backward compatibility
export const ExperienceOptions = ExperienceHistogram;
